import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/axiosConfig';

const DashboardContext = createContext();

export const useDashboard = () => useContext(DashboardContext);

function normalizeLayout(layout) {
  return (layout || []).map((item, index) => ({
    x: typeof item.x === 'number' ? item.x : 0,
    y: typeof item.y === 'number' ? item.y : index * 4,
    w: typeof item.w === 'number' ? item.w : 6,
    h: typeof item.h === 'number' ? item.h : 4,
    i: item.i,
  }));
}

/** Phone-only catalog keys; web library must never list them (see widget_library.platform). */
function filterPhoneWidgetsFromLibrary(widgetsByCategory) {
  const next = {};
  Object.entries(widgetsByCategory || {}).forEach(([cat, list]) => {
    const items = (list || []).filter(
      (w) => w?.widget_key && !String(w.widget_key).startsWith('mobile_')
    );
    if (items.length) next[cat] = items;
  });
  return next;
}

export const DashboardProvider = ({ children }) => {
  const [currentContext, setCurrentContext] = useState(null);
  const [layout, setLayout] = useState([]);
  const [widgetConfigs, setWidgetConfigs] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetLibrary, setWidgetLibrary] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [templateStale, setTemplateStale] = useState(false);
  const [templateRevision, setTemplateRevision] = useState(1);

  const applyWidgetsPayload = useCallback((data) => {
    setLayout(normalizeLayout(data.layout));
    setWidgetConfigs(data.widgetConfigs || {});
    setTemplateStale(!!data.templateStale);
    setTemplateRevision(
      data.templateRevision != null ? Number(data.templateRevision) : 1
    );
  }, []);

  const fetchWidgetsForContext = useCallback(
    async (contextId) => {
      const widgetResponse = await api.get(`/api/contexts/${contextId}/widgets`);
      applyWidgetsPayload(widgetResponse.data);
    },
    [applyWidgetsPayload]
  );

  const loadCurrentContext = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/api/contexts/current');
      const context = response.data.context;
      setCurrentContext(context);

      await fetchWidgetsForContext(context.context_id);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWidgetsForContext]);

  useEffect(() => {
    loadCurrentContext();
  }, [loadCurrentContext]);

  const switchContext = useCallback(
    async (contextId) => {
      try {
        setIsLoading(true);
        await api.post('/api/contexts/switch', { contextId });

        const ctxResponse = await api.get(`/api/contexts/${contextId}/info`);
        setCurrentContext(ctxResponse.data.context);

        await fetchWidgetsForContext(contextId);
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Failed to switch context:', err);
        setError('Failed to switch context');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWidgetsForContext]
  );

  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    setHasUnsavedChanges(true);
  }, []);

  const addWidget = useCallback((widgetKey, position = null) => {
    const widget = Object.values(widgetLibrary)
      .flat()
      .find(w => w.widget_key === widgetKey);

    if (!widget) return;
    if (layout.find(item => item.i === widgetKey)) return;

    const defaultSize = widget.default_size || { w: 4, h: 3 };
    const maxY = layout.length > 0
      ? Math.max(...layout.map(item => item.y + item.h))
      : 0;

    const newItem = {
      i: widgetKey,
      x: position?.x ?? 0,
      y: position?.y ?? maxY,
      w: position?.w ?? defaultSize.w ?? 4,
      h: position?.h ?? defaultSize.h ?? 3,
    };

    setLayout(prev => [...prev, newItem]);
    setWidgetConfigs(prev => ({
      ...prev,
      [widgetKey]: {
        name: widget.name,
        description: widget.description,
        category: widget.category,
        icon: widget.icon,
        is_locked: false,
        config: {},
      },
    }));
    setHasUnsavedChanges(true);
  }, [widgetLibrary, layout]);

  const removeWidget = useCallback((widgetKey) => {
    setLayout(prev => prev.filter(item => item.i !== widgetKey));
    setWidgetConfigs(prev => {
      const next = { ...prev };
      delete next[widgetKey];
      return next;
    });
    setHasUnsavedChanges(true);
  }, []);

  const saveLayout = useCallback(async () => {
    if (!currentContext) return;
    try {
      await api.put(`/api/contexts/${currentContext.context_id}/layout`, {
        layout,
        widgetConfigs,
      });
      setHasUnsavedChanges(false);
      await fetchWidgetsForContext(currentContext.context_id);
    } catch (err) {
      console.error('Failed to save layout:', err);
      setError('Failed to save layout');
    }
  }, [currentContext, layout, widgetConfigs, fetchWidgetsForContext]);

  const resetToTemplate = useCallback(async () => {
    if (!currentContext) return;
    try {
      setIsLoading(true);
      await api.post(`/api/contexts/${currentContext.context_id}/reset`);
      await loadCurrentContext();
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to reset:', err);
      setError('Failed to reset');
    } finally {
      setIsLoading(false);
    }
  }, [currentContext, loadCurrentContext]);

  const acknowledgeTemplateUpdate = useCallback(async () => {
    if (!currentContext) return;
    try {
      try {
        await api.post(`/api/contexts/${currentContext.context_id}/ack-template`);
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
      await fetchWidgetsForContext(currentContext.context_id);
    } catch (err) {
      console.error('Failed to acknowledge template:', err);
      setError('Failed to update preference');
    }
  }, [currentContext, fetchWidgetsForContext]);

  const publishRoleTemplate = useCallback(async () => {
    if (!currentContext) return;
    try {
      await api.post(
        `/api/contexts/${currentContext.context_id}/publish-template`,
        { layout, widgetConfigs }
      );
      setHasUnsavedChanges(false);
      await fetchWidgetsForContext(currentContext.context_id);
    } catch (err) {
      console.error('Failed to publish template:', err);
      setError(
        err.response?.data?.error || 'Failed to publish template'
      );
      throw err;
    }
  }, [currentContext, layout, widgetConfigs, fetchWidgetsForContext]);

  const loadWidgetLibrary = useCallback(async () => {
    if (!currentContext) return;
    try {
      const response = await api.get(
        `/api/contexts/${currentContext.context_id}/widget-library`
      );
      setWidgetLibrary(
        filterPhoneWidgetsFromLibrary(response.data.widgetsByCategory || {})
      );
    } catch (err) {
      console.error('Failed to load widget library:', err);
    }
  }, [currentContext]);

  const toggleEditMode = useCallback(async () => {
    if (isEditMode && hasUnsavedChanges) {
      await saveLayout();
    }
    if (!isEditMode) {
      await loadWidgetLibrary();
    }
    setHasUnsavedChanges(false);
    setIsEditMode(prev => !prev);
  }, [isEditMode, hasUnsavedChanges, saveLayout, loadWidgetLibrary]);

  return (
    <DashboardContext.Provider
      value={{
        currentContext, layout, widgetConfigs, isLoading, error,
        isEditMode, widgetLibrary, hasUnsavedChanges,
        templateStale, templateRevision,
        loadCurrentContext, switchContext,
        handleLayoutChange, addWidget, removeWidget,
        saveLayout, resetToTemplate, toggleEditMode,
        acknowledgeTemplateUpdate, publishRoleTemplate,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
